import type { TenantScope } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import useAllProxyConfigs from '@/hooks/use-all-proxy-configs';

// These aren't sent to the API - just used to represent all the options of decryptable things in
// the MultiSelect. We need this since different decrypt options have special serializations in the API
export enum VaultProxyOptionKind {
  all = 'any',
  jit = 'just_in_time',
}

export type VaultProxyOption = VaultProxyOptionKind | string;

export const vaultProxyOptionFromTenantScope = (scope: TenantScope): VaultProxyOption | undefined => {
  if (scope.kind !== 'invoke_vault_proxy') {
    return undefined;
  }
  if (scope.data.kind === 'just_in_time') {
    return VaultProxyOptionKind.jit;
  }
  if (scope.data.kind === 'any') {
    return VaultProxyOptionKind.all;
  }
  return scope.data.id;
};

export const tenantScopeFromVaultProxyOption = (option: VaultProxyOption): TenantScope => {
  if (option === VaultProxyOptionKind.all || option === VaultProxyOptionKind.jit) {
    return { kind: 'invoke_vault_proxy', data: { kind: option } };
  }
  return {
    kind: 'invoke_vault_proxy',
    data: { kind: 'id', id: option },
  };
};

type Option = {
  label: string;
  value: string;
};

const useVaultProxyOptions = () => {
  const { t } = useTranslation('roles', {
    keyPrefix: 'scopes',
  });
  const { data } = useAllProxyConfigs({ status: 'enabled' });

  const vaultProxyOptions: Option[] =
    data?.map(c => ({
      value: c.id,
      label: c.name,
    })) ?? [];
  const options: Option[] = [
    {
      value: VaultProxyOptionKind.jit as VaultProxyOption,
      label: t($ => $.invoke_vault_proxy_options.jit),
    },
  ].concat(vaultProxyOptions);
  const allOption: Option = {
    value: VaultProxyOptionKind.all,
    label: t($ => $.invoke_vault_proxy_options.all),
  };
  const allOptions = options.concat([allOption]);
  return { options, allOption, allOptions };
};

export default useVaultProxyOptions;
